//

import Dexie from "dexie"
import { mapLanguageValueToKey } from "@/lang/config"
import {
	inferMediaKind,
	registerGameMediaUrlKind,
} from "@/utils/gameMedia"
import type { GameMediaKind } from "@/utils/gameMedia"

/**
 * Dedicated DB for event media cache.
 * IMPORTANT: do NOT reuse the legacy "Content" Dexie DB for media — it was upgraded
 * to many dynamic per-field tables and can hang on open() (stuck download at 0/N).
 * iPad Hunt answers/uploads still use "Content" via ipadHuntUtils.
 */
const MEDIA_DB_NAME = "ContentMediaCache"
const MEDIA_STORE = "content_media"

const mediaDb = new Dexie(MEDIA_DB_NAME)
mediaDb.version(1).stores({
	[MEDIA_STORE]: "cacheKey, url",
})

let mediaDbReady: Promise<Dexie> | null = null

const pendingDownloads = new Map<string, Promise<Blob>>()

export type MissingMediaItem = {
	contentName: string
	instance: Record<string, unknown> & { id: string | number }
	key: string
	url: string
	cacheKey: string
	mediaKind: GameMediaKind
}

const MEDIA_DOWNLOAD_CONCURRENCY = 2

const mediaCacheKey = (
	contentName: string,
	fieldKey: string,
	recordId: string | number
) => `${contentName}_${fieldKey}_${recordId}`

const initMediaDB = async () => {
	if (!mediaDbReady) {
		mediaDbReady = mediaDb.open().catch((error) => {
			mediaDbReady = null
			throw error
		})
	}
	return mediaDbReady
}

/** @deprecated Prefer initMediaDB — kept for callers that still import initDB. */
const initDB = async () => initMediaDB()

const normalizeUrl = (url: string) => url.split("?")[0]

const MEDIA_FILE_REGEX =
	/\.(png|jpe?g|gif|webp|svg|avif|heic|heif|mp4|mov|webm|avi|m4v|mkv|mp3|wav|ogg|m4a|aac|flac|opus)(\?.*)?$/i
const LANGUAGE_TOKEN_REGEX = /(^|[/_.-])(es|en|ca)(?=$|[/_.-])/i

const getEventLanguage = (
	instance: Record<string, unknown> & { id: string | number }
) => {
	const eventLanguageRaw =
		(instance.event_language as string | undefined) ||
		(instance.language_event as string | undefined) ||
		(instance.language as string | undefined) ||
		localStorage.getItem("language") ||
		""

	return eventLanguageRaw.toLowerCase().trim()
}

const getUrlLanguageToken = (url: string) => {
	const normalized = normalizeUrl(url).toLowerCase()
	const match = normalized.match(LANGUAGE_TOKEN_REGEX)
	return match?.[2] || null
}

const getKeyLanguageToken = (key: string) => {
	const normalizedKey = key.toLowerCase()
	const match = normalizedKey.match(
		/(?:^|[_-])(es|en|lang1|lang2|lang3)(?:$|[_-])/
	)
	return match?.[1] || null
}

const shouldDownloadMediaForLanguage = (
	key: string,
	value: string,
	instance: Record<string, unknown> & { id: string | number }
) => {
	const eventLanguage = getEventLanguage(instance)
	if (!eventLanguage) {
		return true
	}

	const mediaLanguageRaw = (instance.language as string | undefined) || ""
	const mediaLanguage = mediaLanguageRaw.toLowerCase().trim()
	if (mediaLanguage && mediaLanguage !== eventLanguage) {
		return false
	}

	const keyLanguageToken = getKeyLanguageToken(key)
	if (keyLanguageToken) {
		const eventLanguageKey = mapLanguageValueToKey(eventLanguage)?.toLowerCase()
		const validLanguageTokens = new Set(
			[
				eventLanguage === "es" || eventLanguage === "en" ? eventLanguage : null,
				eventLanguageKey,
			].filter(Boolean) as string[]
		)
		if (!validLanguageTokens.has(keyLanguageToken)) {
			return false
		}
	}

	const urlLanguageToken = getUrlLanguageToken(value)
	if (urlLanguageToken && urlLanguageToken !== eventLanguage) {
		return false
	}

	return true
}

const fetchBlobOnce = async (url: string) => {
	if (!pendingDownloads.has(url)) {
		pendingDownloads.set(
			url,
			fetch(url)
				.then(async (res) => {
					if (!res.ok) {
						throw new Error(`Failed to fetch ${url}: ${res.status}`)
					}
					return res.blob()
				})
				.finally(() => {
					pendingDownloads.delete(url)
				})
		)
	}
	return pendingDownloads.get(url)!
}

const createCachedObjectUrl = (
	blob: Blob,
	originalUrl: string,
	kind?: GameMediaKind
) => {
	const objectUrl = URL.createObjectURL(blob)
	registerGameMediaUrlKind(
		objectUrl,
		kind ?? inferMediaKind(originalUrl, blob.type)
	)
	return objectUrl
}

const waitUntilOnline = () => {
	if (typeof navigator === "undefined" || navigator.onLine) {
		return Promise.resolve()
	}
	return new Promise<void>((resolve) => {
		const onOnline = () => {
			window.removeEventListener("online", onOnline)
			resolve()
		}
		window.addEventListener("online", onOnline)
	})
}

type MediaRecord = { cacheKey: string; file: Blob; url: string }

const getCachedMediaRecord = async (
	cacheKey: string,
	url: string
): Promise<MediaRecord | undefined> => {
	const record = (await mediaDb.table(MEDIA_STORE).get(cacheKey)) as
		| MediaRecord
		| undefined
	if (
		record?.file &&
		record?.url &&
		normalizeUrl(record.url) === normalizeUrl(url)
	) {
		return record
	}
	return undefined
}

/**
 * Apply IndexedDB cache hits as blob: URLs. Returns media fields that still need a network fetch.
 */
const applyCachedMedia = async (
	contentName: string,
	instance: Record<string, unknown> & { id: string | number }
): Promise<MissingMediaItem[]> => {
	await initMediaDB()
	const misses: MissingMediaItem[] = []

	for (const key in instance) {
		try {
			const value = instance[key]

			if (
				typeof value !== "string" ||
				!value.includes("digitaloceanspaces.com") ||
				key.includes("pdf") ||
				!MEDIA_FILE_REGEX.test(normalizeUrl(value))
			) {
				continue
			}

			if (!shouldDownloadMediaForLanguage(key, value, instance)) {
				continue
			}

			const mediaKind = inferMediaKind(value)
			const cacheKey = mediaCacheKey(contentName, key, instance.id)
			const record = await getCachedMediaRecord(cacheKey, value)

			if (record?.file) {
				instance[key] = createCachedObjectUrl(record.file, value, mediaKind)
				continue
			}

			misses.push({
				contentName,
				instance,
				key,
				url: value,
				cacheKey,
				mediaKind,
			})
		} catch (error) {
			console.error("Error applying cached media:", error)
		}
	}

	return misses
}

/** Download one missing media item into IndexedDB and replace the field with a blob: URL. */
const downloadMissingMediaItem = async (item: MissingMediaItem) => {
	try {
		if (!navigator.onLine) {
			await waitUntilOnline()
		}

		await initMediaDB()

		const existing = await getCachedMediaRecord(item.cacheKey, item.url)
		if (existing?.file) {
			const current = item.instance[item.key]
			if (
				typeof current === "string" &&
				(current === item.url ||
					normalizeUrl(current) === normalizeUrl(item.url) ||
					current.startsWith("http"))
			) {
				item.instance[item.key] = createCachedObjectUrl(
					existing.file,
					item.url,
					item.mediaKind
				)
			}
			return
		}

		const blob = await fetchBlobOnce(item.url)
		await mediaDb.table(MEDIA_STORE).put({
			cacheKey: item.cacheKey,
			file: blob,
			url: item.url,
		})

		const current = item.instance[item.key]
		if (
			typeof current === "string" &&
			(current === item.url ||
				normalizeUrl(current) === normalizeUrl(item.url) ||
				current.startsWith("http"))
		) {
			item.instance[item.key] = createCachedObjectUrl(
				blob,
				item.url,
				item.mediaKind
			)
		}
	} catch (error) {
		console.error("Error downloading missing media:", error)
	}
}

type DownloadQueueOptions = {
	concurrency?: number
	onItemDone?: () => void
}

/**
 * Download misses with limited concurrency. Pauses while offline and resumes on `online`.
 */
const downloadMissingMediaQueue = async (
	items: MissingMediaItem[],
	options: DownloadQueueOptions = {}
) => {
	if (items.length === 0) {
		return
	}

	const concurrency = Math.max(
		1,
		Math.min(options.concurrency ?? MEDIA_DOWNLOAD_CONCURRENCY, items.length)
	)
	let nextIndex = 0

	const worker = async () => {
		while (nextIndex < items.length) {
			if (typeof navigator !== "undefined" && !navigator.onLine) {
				await waitUntilOnline()
			}
			const item = items[nextIndex++]
			await downloadMissingMediaItem(item)
			options.onItemDone?.()
		}
	}

	await Promise.all(Array.from({ length: concurrency }, () => worker()))
}

/**
 * Legacy helper: apply cache hits then download misses (videos fire-and-forget).
 */
const checkImageDb = async (
	contentName: string,
	instance: Record<string, unknown> & { id: string | number }
) => {
	const misses = await applyCachedMedia(contentName, instance)
	for (const miss of misses) {
		if (miss.mediaKind === "video") {
			void downloadMissingMediaItem(miss)
			continue
		}
		await downloadMissingMediaItem(miss)
	}
	return instance
}

export {
	initDB,
	initMediaDB,
	checkImageDb,
	applyCachedMedia,
	downloadMissingMediaItem,
	downloadMissingMediaQueue,
}
